/**
 * Public, read-only projection of a trip itinerary served to anyone holding a trip's share link
 * (`POST /api/share/itinerary`). Recipients are unauthenticated, so this is a hard security
 * boundary: the server builds every field from an explicit allowlist and then parses the result
 * through these schemas.
 *
 * Every schema here is a plain `z.object` (never `looseObject`) on purpose — parsing strips any key
 * not listed, so a field accidentally copied into the projection (a booking confirmation, a cost, a
 * note) is dropped before it reaches the wire. Booking data, costs, notes, attachments, owner
 * identity, internal ids and the token itself are deliberately absent from these shapes.
 */

import { z } from 'zod';

import { ItineraryItemEventTypeSchema } from './itinerary-item-event-schemas.js';

/** Airline identity shown on a shared flight leg. */
export const SharedAirlineSchema = z.object({
  iata_code: z.string(),
  name: z.string(),
  logo_url: z.string().optional(),
});

/** Airport identity shown on a shared flight leg. */
export const SharedAirportSchema = z.object({
  iata_code: z.string(),
  airport_name: z.string(),
  city_name: z.string().optional(),
  country_name: z.string().optional(),
  country_code: z.string().optional(),
});

/**
 * A single flight leg. A strict subset of `FlightSegmentSchema`: cabin class and fare amenities are
 * left out because they reveal what the traveler paid for.
 */
export const SharedFlightSegmentSchema = z.object({
  airline: SharedAirlineSchema,
  flightNumber: z.string(),
  aircraftType: z.string().optional(),
  operatedBy: z.string().optional(),
  departure: z.string(),
  arrival: z.string(),
  origin: SharedAirportSchema,
  destination: SharedAirportSchema,
  duration: z.number().optional(),
  overnight: z.boolean().optional(),
});

export type SharedFlightSegment = z.infer<typeof SharedFlightSegmentSchema>;

/** Public venue details (a hotel for stay events, a place for place events). */
export const SharedVenueDetailsSchema = z.object({
  name: z.string(),
  address: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  images: z.array(z.string()).max(4),
  location: z.object({ lat: z.number(), lng: z.number() }).optional(),
});

export type SharedVenueDetails = z.infer<typeof SharedVenueDetailsSchema>;

/**
 * Public destination details for a "welcome" (destination arrival) block: the catalog destination's
 * description, photos and travel tips. Editorial catalog content only; nothing about the traveler.
 */
export const SharedDestinationDetailsSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  images: z.array(z.string()).max(4),
  logistics: z
    .object({
      tippingCulture: z.string().optional(),
      accessibility: z.string().optional(),
      emergencyNumbers: z
        .object({
          police: z.union([z.string(), z.number()]),
          medical: z.union([z.string(), z.number()]),
          fire: z.union([z.string(), z.number()]),
        })
        .optional(),
      powerOutletInfo: z.object({ type: z.array(z.string()), voltage: z.string(), frequency: z.string() }).optional(),
      travel_advisories: z.string().optional(),
    })
    .optional(),
});

export type SharedDestinationDetails = z.infer<typeof SharedDestinationDetailsSchema>;

const SharedEventBaseSchema = z.object({
  /** Opaque per-response id (`e1`, `e2`, ...); never a Firestore id. */
  id: z.string(),
  /** Opaque per-response source-item id (`i1`, ...); groups legs of one flight. */
  itemId: z.string(),
  title: z.string(),
  dateTime: z.string(),
  sortPriority: z.number().optional(),
});

const SharedFlightEventMetadataSchema = z.object({
  segment: SharedFlightSegmentSchema,
  duration: z.number().optional(),
  isFirstSegment: z.boolean().optional(),
});

/** Event types whose metadata is a flight segment. */
const FLIGHT_EVENT_TYPES = ['flight_departure', 'flight_arrival', 'layover', 'destination_arrival'] as const;

/**
 * Flight-segment event types shared as-is. Plain flight arrivals are never shared (the departure
 * block shows the landing), and destination arrivals only with a matched destination (below).
 */
const FLIGHT_SEGMENT_EVENT_TYPES = ['flight_departure', 'layover'] as const;

/**
 * Event types rendered as a venue (hotel or place) or a plain activity. Hotel check-outs are never
 * shared: they add nothing for someone following the trip.
 */
const VENUE_EVENT_TYPES = ItineraryItemEventTypeSchema.exclude([...FLIGHT_EVENT_TYPES, 'hotel_checkout']);

/**
 * A display event in the shared itinerary. Mirrors `ItineraryItemEventSchema` closely enough that
 * the client's `ItineraryItemEventModel.from()` formats it unchanged, but with metadata restricted to
 * what the read-only page renders and an optional `details` block for venues.
 */
export const SharedItineraryEventSchema = z.discriminatedUnion('type', [
  SharedEventBaseSchema.extend({ type: z.enum(FLIGHT_SEGMENT_EVENT_TYPES), metadata: SharedFlightEventMetadataSchema }),
  SharedEventBaseSchema.extend({
    type: z.literal('destination_arrival'),
    metadata: SharedFlightEventMetadataSchema,
    destination: SharedDestinationDetailsSchema,
  }),
  SharedEventBaseSchema.extend({
    type: VENUE_EVENT_TYPES,
    metadata: z
      .object({
        originalDateTime: z.string().optional(),
        placePrimaryType: z.string().optional(),
      })
      .optional(),
    details: SharedVenueDetailsSchema.optional(),
  }),
]);

export type SharedItineraryEvent = z.infer<typeof SharedItineraryEventSchema>;

/** One calendar day of the shared itinerary, in display order. */
export const SharedItineraryDaySchema = z.object({
  dateKey: z.string().describe('Local calendar-date key (YYYY-MM-DD)'),
  dayNumber: z.number().int(),
  isTravelDay: z.boolean(),
  title: z.string().optional().describe('AI-generated day title'),
  events: z.array(SharedItineraryEventSchema),
});

export type SharedItineraryDay = z.infer<typeof SharedItineraryDaySchema>;

/** Trip-level header fields for the shared page. No owner identity, status or ids. */
export const SharedTripSchema = z.object({
  title: z.string(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  destinationNames: z.array(z.string()),
  coverImageUrl: z.string().optional(),
  summary: z.string().optional(),
});

export type SharedTrip = z.infer<typeof SharedTripSchema>;

/** Response body of `POST /api/share/itinerary`. */
/**
 * A run of consecutive days spent at one destination, as the itinerary builder groups them
 * (`groupDaysByDestination`). Only the destination's display name and its days — no costs.
 */
export const SharedItinerarySectionSchema = z.object({
  label: z.string(),
  dateKeys: z.array(z.string()).describe('The section\'s days, as `SharedItineraryDay.dateKey`s in order'),
});

export type SharedItinerarySection = z.infer<typeof SharedItinerarySectionSchema>;

export const SharedItineraryResponseSchema = z.object({
  trip: SharedTripSchema,
  days: z.array(SharedItineraryDaySchema),
  /**
   * Destination sections covering `days` in order, for the overview list. Defaults to empty so a
   * client deployed ahead of the server still parses the older response.
   */
  sections: z.array(SharedItinerarySectionSchema).default([]),
  membership: z.object({
    /** The owner's referral code, so a recipient's membership application credits the owner. */
    referralCode: z.string().optional(),
  }),
});

export type SharedItineraryResponse = z.infer<typeof SharedItineraryResponseSchema>;

/** Request body of `POST /api/share/itinerary`. The token travels in the body to keep it out of request logs. */
export const SharedItineraryRequestSchema = z.object({ token: z.string() });

export type SharedItineraryRequest = z.infer<typeof SharedItineraryRequestSchema>;
